import { useState, useCallback } from 'react';

export interface PaginationState {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface UsePaginationResult {
  pagination: PaginationState;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setPaginationData: (data: Partial<PaginationState>) => void;
  nextPage: () => void;
  previousPage: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
  resetPagination: () => void;
}

const DEFAULT_PAGINATION: PaginationState = {
  page: 1,
  limit: 20,
  total: 0,
  pages: 0,
};

export const usePagination = (initialLimit: number = 20): UsePaginationResult => {
  const [pagination, setPagination] = useState<PaginationState>({
    ...DEFAULT_PAGINATION,
    limit: initialLimit,
  });

  const setPage = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  }, []);

  const setPaginationData = useCallback((data: Partial<PaginationState>) => {
    setPagination(prev => ({ ...prev, ...data }));
  }, []);

  const nextPage = useCallback(() => {
    setPagination(prev => {
      if (prev.page < prev.pages) {
        return { ...prev, page: prev.page + 1 };
      }
      return prev;
    });
  }, []);

  const previousPage = useCallback(() => {
    setPagination(prev => {
      if (prev.page > 1) {
        return { ...prev, page: prev.page - 1 };
      }
      return prev;
    });
  }, []);

  const canGoNext = pagination.page < pagination.pages;
  const canGoPrevious = pagination.page > 1;

  const resetPagination = useCallback(() => {
    setPagination({ ...DEFAULT_PAGINATION, limit: initialLimit });
  }, [initialLimit]);

  return {
    pagination,
    setPage,
    setLimit,
    setPaginationData,
    nextPage,
    previousPage,
    canGoNext,
    canGoPrevious,
    resetPagination,
  };
};
